import { getPlatform } from './platform.utils';

/**
 * Version Manager Utilities
 * Utilities for version comparison and update checking
 */

/**
 * Compare two version strings
 * @param {string} version1 - First version
 * @param {string} version2 - Second version
 * @returns {number} -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
export const compareVersions = (version1, version2) => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  const maxLength = Math.max(v1Parts.length, v2Parts.length);
  
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    
    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }
  
  return 0;
};

/**
 * Check if version is less than another
 * @param {string} version1 - First version
 * @param {string} version2 - Second version
 * @returns {boolean} True if v1 < v2
 */
export const isVersionLessThan = (version1, version2) => {
  return compareVersions(version1, version2) < 0;
};

/**
 * Check if version is greater than another
 * @param {string} version1 - First version
 * @param {string} version2 - Second version
 * @returns {boolean} True if v1 > v2
 */
export const isVersionGreaterThan = (version1, version2) => {
  return compareVersions(version1, version2) > 0;
};

/**
 * Check if update is required
 * @param {string} currentVersion - Current app version
 * @param {string} latestVersion - Latest available version
 * @param {string} minSupportedVersion - Minimum supported version
 * @returns {Object} Update status and type
 */
export const checkUpdateRequired = (currentVersion, latestVersion, minSupportedVersion) => {
  // Check if current version is less than minimum supported (force update)
  const isForceUpdate = isVersionLessThan(currentVersion, minSupportedVersion);
  
  // Check if update is available (partial update)
  const isUpdateAvailable = isVersionLessThan(currentVersion, latestVersion);
  
  let updateType = 'none';
  if (isForceUpdate) {
    updateType = 'force';
  } else if (isUpdateAvailable) {
    updateType = 'partial';
  }
  
  return {
    updateRequired: isForceUpdate || isUpdateAvailable,
    updateType,
    currentVersion,
    latestVersion,
    minSupportedVersion
  };
};

/**
 * Get current platform version info from config
 * @returns {Object} Current platform version info
 */
export const getCurrentPlatformVersion = () => {
  try {
    // This would read from version.config.json
    // For now, return platform-specific defaults
    const platform = getPlatform();
    
    if (platform === 'android') {
      return {
        version: '1.0.0',
        versionCode: 1,
        minSupportedVersion: '1.0.0',
        minSupportedVersionCode: 1
      };
    } else if (platform === 'ios') {
      return {
        version: '1.0.0',
        buildNumber: '1',
        minSupportedVersion: '1.0.0',
        minSupportedBuildNumber: 1
      };
    } else {
      return {
        version: '1.0.0',
        build: '1'
      };
    }
  } catch (error) {
    console.error('Failed to get platform version:', error);
    return null;
  }
};

/**
 * Format version for API requests
 * @param {string} version - Version string
 * @returns {Object} Formatted version object
 */
export const formatVersionForAPI = (version) => {
  if (!version) return null;
  
  const parts = version.split('.');
  return {
    major: parseInt(parts[0] || 0),
    minor: parseInt(parts[1] || 0),
    patch: parseInt(parts[2] || 0),
    full: version
  };
};

/**
 * Get update check parameters
 * @returns {Object} Parameters for version check API
 */
export const getVersionCheckParams = () => {
  const platform = getPlatform();
  const currentVersion = getCurrentPlatformVersion();
  
  return {
    platform,
    currentVersion: currentVersion?.version || '1.0.0',
    versionCode: currentVersion?.versionCode || currentVersion?.buildNumber || 1
  };
};
